import PropTypes from 'prop-types';
import cn from 'classnames';

import arrow from 'assets/arrow.svg';
import capturedIcon from 'assets/auditCompletedIcon.svg';

import { LANDING_PAGE_AUDIT } from 'Pages/TeamDashboard/CreateReview/components/constants';

import styles from './styles.module.scss';

// Flow selection options. Set `hidden: true` to take an option off the screen.
const GENERAL_OPTIONS = [
  {
    id: 'ux_critique',
    title: 'Usability Audit',
    description: 'Find where users get stuck, confused, or drop off before completing their goal',
    emoji: '🧠',
  },
  {
    id: 'growth-audit',
    title: 'Growth Audit',
    description: 'Spot the gaps limiting engagement and trust, and what\'s driving them',
    emoji: '📈',
  },
  {
    id: 'new_ideas',
    title: 'New Ideas & Prototypes',
    description: 'Strategic growth ideas with ready-to-test prototypes and A/B variants',
    emoji: '💡',
    hidden: true,
  },
  {
    id: 'onboarding_flow',
    title: 'User Onboarding',
    description: 'Help users reach the Aha moment faster and drive higher activation',
    emoji: '🎯',
  },
  // Takes a URL to capture instead of leading to screen selection
  {
    id: LANDING_PAGE_AUDIT.KEY,
    title: 'Landing Page Growth',
    description: 'Identify what\'s causing visitors to bounce, and get prioritized fixes to lift conversion',
    emoji: '🌐',
  },
];

const VISIBLE_OPTIONS = GENERAL_OPTIONS.filter(option => !option.hidden);

const FlowSelection = ({
  selectedFlow,
  onFlowSelect,
  onNext,
  className,
  landingPageUrl,
  onLandingPageUrlChange,
  onCaptureLandingPage,
  onAddToContext,
  isCapturing,
  isLandingPageCaptured,
  landingPageError,
}) => {
  const isLandingPageSelected = selectedFlow === LANDING_PAGE_AUDIT.KEY;
  const isCaptureDisabled = isCapturing || !landingPageUrl.trim();
  const isNextDisabled = isLandingPageSelected
    ? isCapturing || !isLandingPageCaptured
    : !selectedFlow;

  const handleFlowSelect = flowId => {
    if (flowId !== selectedFlow) {
      onFlowSelect(flowId);
    }
  };

  const handleNext = () => {
    if (isNextDisabled) {
      return;
    }

    if (isLandingPageSelected) {
      onAddToContext?.();
    }
    else if (onNext) {
      onNext();
    }
  };

  const handleCapture = () => {
    if (!isCaptureDisabled) {
      onCaptureLandingPage?.();
    }
  };

  const handleUrlKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleCapture();
    }
  };

  const captureButtonLabel = isCapturing
    ? 'Capturing...'
    : isLandingPageCaptured ? 'Capture again' : 'Capture page';

  const captureHelperText = landingPageError
    || (isLandingPageCaptured
      ? 'This page will be added to your product library'
      : 'It takes a few seconds to capture');

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.content}>
        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.title}>
            Select your audit
          </h1>
        </div>

        {/* Review Types Section */}
        <div className={styles.reviewTypes}>
          {/* General Section */}
          <div className={styles.section}>
            <div className={styles.options}>
              {VISIBLE_OPTIONS.map(option => (
                <button
                  key={option.id}
                  className={cn(styles.card, {
                    [styles.cardSelected]: selectedFlow === option.id,
                  })}
                  onClick={() => handleFlowSelect(option.id)}
                >
                  <div className={styles.cardTitle}>
                    <span className={styles.cardEmoji}>
                      {option.emoji}
                    </span>
                    <span className={styles.cardTitleText}>
                      {option.title}
                    </span>
                  </div>
                  <div className={styles.cardDescription}>
                    {option.description}
                  </div>
                </button>
              ))}
            </div>

            {isLandingPageSelected && (
              <div className={styles.landingCapture}>
                <div className={styles.captureRow}>
                  <div className={styles.urlInputWrap}>
                    {isLandingPageCaptured && !isCapturing && (
                      <img src={capturedIcon} alt="" className={styles.capturedIcon} />
                    )}
                    <input
                      type="url"
                      inputMode="url"
                      autoComplete="url"
                      spellCheck={false}
                      autoFocus
                      aria-label="Website URL"
                      aria-invalid={Boolean(landingPageError)}
                      placeholder="e.g. acme.com"
                      className={cn(styles.urlInput, {
                        [styles.urlInputCaptured]: isLandingPageCaptured && !isCapturing,
                        [styles.urlInputError]: landingPageError,
                      })}
                      value={landingPageUrl}
                      disabled={isCapturing}
                      onChange={event => onLandingPageUrlChange?.(event.target.value)}
                      onKeyDown={handleUrlKeyDown}
                    />
                  </div>
                  <button
                    type="button"
                    className={cn(styles.captureButton, {
                      [styles.captureButtonSecondary]: isLandingPageCaptured && !isCapturing,
                    })}
                    onClick={handleCapture}
                    disabled={isCaptureDisabled}
                  >
                    {captureButtonLabel}
                  </button>
                </div>
                <div
                  className={cn(styles.captureHelper, { [styles.captureHelperError]: landingPageError })}
                  role={landingPageError ? 'alert' : undefined}
                >
                  {captureHelperText}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.buttons}>
          <button
            className={cn(styles.button, styles.buttonNext, {
              [styles.buttonDisabled]: isNextDisabled,
            })}
            onClick={handleNext}
            disabled={isNextDisabled}
          >
            {isLandingPageSelected ? 'Add your context' : 'Select your screens'}
            <img src={arrow} />
          </button>
        </div>
      </div>
    </div>
  );
};

FlowSelection.propTypes = {
  selectedFlow: PropTypes.string,
  onFlowSelect: PropTypes.func.isRequired,
  onNext: PropTypes.func,
  className: PropTypes.string,
  landingPageUrl: PropTypes.string,
  onLandingPageUrlChange: PropTypes.func,
  onCaptureLandingPage: PropTypes.func,
  onAddToContext: PropTypes.func,
  isCapturing: PropTypes.bool,
  isLandingPageCaptured: PropTypes.bool,
  landingPageError: PropTypes.string,
};

FlowSelection.defaultProps = {
  selectedFlow: null,
  onNext: null,
  className: '',
  landingPageUrl: '',
  onLandingPageUrlChange: null,
  onCaptureLandingPage: null,
  onAddToContext: null,
  isCapturing: false,
  isLandingPageCaptured: false,
  landingPageError: null,
};

export default FlowSelection;
